<script setup lang="ts">
    import CircleLoader from '@shared/components/ui/circle-loader.vue';

    const props = withDefaults(defineProps<{
        step?: number
        title?: string
        subtitle?: string
        background?: boolean
        animate?: boolean
    }>(), {
        step: 0,
        title: '',
        subtitle: '',
    });

    const emit = defineEmits<{(event: 'next', value: number)}>();

    const ENTRY_DELAY = 100;
    const EXIT_DELAY = 1000;
    const STEP_MIDDLE = 0.5;
    const STEP_NEXT = 1;
    const WAITING_DELAY = 2000;

    const showElement = ref(false);
    const exitElement = ref(false);

    const handleExitStep = ():void => {
        exitElement.value = true;

        if (!props.animate) {
            emit('next', props.step + STEP_NEXT);
            return;
        }

        emit('next', props.step + STEP_MIDDLE);

        setTimeout(() => {
            emit('next', props.step + STEP_NEXT);
        }, EXIT_DELAY);
    };

    const resetAnimationState = ():void => {
        showElement.value = false;
        exitElement.value = false;

        // Trigger the "showElement" transition again after a brief delay
        setTimeout(() => {
            showElement.value = true;
        }, ENTRY_DELAY);
    };

    onMounted(() => {
        resetAnimationState();

        setTimeout(() => {
            handleExitStep();
        }, WAITING_DELAY);
    });
</script>

<template>
    <div
        :class="{
            'slide-bottom-up': showElement && !exitElement && animate,
            'slide-out': exitElement && animate,
            'slide-show': showElement && !exitElement && !animate,
            'loading-card--background': background
        }"
        class="loading-card"
    >
        <div class="loading-card__text">
            <h2 class="loading-card__title">
                {{ title }}
            </h2>
            <span class="loading-card__subtitle">
                {{ subtitle }}
            </span>
        </div>
        <CircleLoader
            :size="100"
            :thickness="7"
        />
    </div>
</template>

<style lang="scss" scoped>
.loading-card {
    position: absolute;
    display: flex;
    overflow: hidden;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    block-size: 100%;
    gap: var(--spacing-07);
    inline-size: 100%;
    inset-block-start: 0;
    transform: translateY(100%);
    transition: all 0.75s ease-in-out;

    h2 {
        font-size: var(--font-h2);
        padding-inline: var(--spacing-03);
        text-align: center;
        text-transform: lowercase;

        &::first-letter {
            text-transform: uppercase;
        }

        @include min-width(md) {
            font-size: var(--font-h1);
        }
    }

    &--background {
        background-color: var(--background-primary);
    }

    &__subtitle {
        font-family: var(--font-secondary);
        font-size: var(--font-h3);
        font-weight: var(--font-weight-medium);
        line-height: 1.2;
        padding-inline: var(--spacing-03);
        text-align: center;
        text-transform: lowercase;

        &::first-letter {
            text-transform: uppercase;
        }
    }

    &__text {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: var(--spacing-03);
        max-inline-size: 400px;
    }

    &.slide-show {
        transform: translateY(0);
        transition: none;
    }

    &.slide-bottom-up {
        transform: translateY(0);
    }

    &.slide-out {
        transform: translateY(-100%);
    }
}
</style>
